import React, { useState, useEffect, useMemo } from "react";
import { Container, Card, Table, Row, Col, Form } from "react-bootstrap";
import { callXeroFunction } from "../firebase.js";
import { useAuth } from "../context/AuthContext.js";
import { useLanguage } from "../context/LanguageContext.js";
import { t } from "../utils/translations.js";

export default function ProfitAnalysisPage() {
  const { user } = useAuth();
  const { language } = useLanguage();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [laborCostPerHour, setLaborCostPerHour] = useState(35);

  // Load clients from PostgreSQL
  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const response = await callXeroFunction('getClients', {}, { method: 'GET' });
      const postgresClients = response.clients || [];

      // Normalizar campos (PostgreSQL usa snake_case)
      const clientsData = postgresClients.map(client => ({
        ...client,
        defaultInvoiceAmount: client.default_price || client.defaultInvoiceAmount,
        hours: client.service_duration || client.hours
      }));
      setClients(clientsData);
    } catch (error) {
      console.error("Error loading clients:", error);
      alert(t(language, "revenueErrorLoading"));
    } finally {
      setLoading(false);
    }
  };

  // Parse time string (HH:MM) to decimal hours
  const parseTimeToHours = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours + (minutes / 60);
  };

  // Calculate profit metrics with useMemo
  const profitMetrics = useMemo(() => {
    return clients.map(client => {
      const invoiceAmount = parseFloat(client.defaultInvoiceAmount) || 0;
      const hours = parseTimeToHours(client.hours);
      const revenuePerHour = hours > 0 ? invoiceAmount / hours : 0;
      const totalLaborCost = hours * laborCostPerHour;
      const totalProfit = invoiceAmount - totalLaborCost;
      const profitPerHour = hours > 0 ? totalProfit / hours : 0;
      const profitMargin = invoiceAmount > 0 ? (totalProfit / invoiceAmount) * 100 : 0;

      return {
        ...client,
        hours,
        invoiceAmount,
        revenuePerHour,
        totalLaborCost,
        totalProfit,
        profitPerHour,
        profitMargin
      };
    }).filter(c => c.invoiceAmount > 0);
  }, [clients, laborCostPerHour]);

  // Calculate totals with useMemo
  const totals = useMemo(() => {
    const totalRevenue = profitMetrics.reduce((sum, c) => sum + c.invoiceAmount, 0);
    const totalLaborCost = profitMetrics.reduce((sum, c) => sum + c.totalLaborCost, 0);
    const totalProfit = profitMetrics.reduce((sum, c) => sum + c.totalProfit, 0);
    const totalHours = profitMetrics.reduce((sum, c) => sum + c.hours, 0);
    const avgProfitPerHour = totalHours > 0 ? totalProfit / totalHours : 0;
    const overallMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
    return { totalRevenue, totalLaborCost, totalProfit, totalHours, avgProfitPerHour, overallMargin };
  }, [profitMetrics]);

  // Sort by profit (highest first)
  const sortedMetrics = useMemo(() =>
    [...profitMetrics].sort((a, b) => b.totalProfit - a.totalProfit),
    [profitMetrics]
  );

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <h2 className="mb-4">
        {t(language, "profitTitle")}
      </h2>

      {/* Labor Cost Setting */}
      <Card className="mb-4 shadow-sm" style={{ backgroundColor: "#fff9c4", border: "2px solid #f9a825" }}>
        <Card.Body>
          <Row className="align-items-center">
            <Col md={8}>
              <h5 className="mb-0" style={{ color: "#f57f17" }}>
                {t(language, "profitLaborConfig")}
              </h5>
              <small className="text-muted">
                {t(language, "profitLaborConfigDesc")}
              </small>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="mb-1 small">{t(language, "profitCostPerHour")}</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={laborCostPerHour}
                  onChange={(e) => setLaborCostPerHour(parseFloat(e.target.value) || 0)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Summary Cards */}
      <Row className="mb-4 g-3">
        <Col xs={6} md={3}>
          <Card className="text-center shadow-sm" style={{ backgroundColor: "#e3f2fd" }}>
            <Card.Body>
              <h6 className="text-muted mb-2">
                {t(language, "profitTotalRevenue")}
              </h6>
              <h3 className="mb-0" style={{ color: "#1976d2" }}>
                ${totals.totalRevenue.toFixed(2)}
              </h3>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="text-center shadow-sm" style={{ backgroundColor: "#ffebee" }}>
            <Card.Body>
              <h6 className="text-muted mb-2">
                {t(language, "profitTotalLaborCost")}
              </h6>
              <h3 className="mb-0" style={{ color: "#c62828" }}>
                ${totals.totalLaborCost.toFixed(2)}
              </h3>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="text-center shadow-sm" style={{ backgroundColor: "#e8f5e9" }}>
            <Card.Body>
              <h6 className="text-muted mb-2">
                {t(language, "profitTotalProfit")}
              </h6>
              <h3 className="mb-0" style={{ color: totals.totalProfit >= 0 ? "#388e3c" : "#c62828" }}>
                ${totals.totalProfit.toFixed(2)}
              </h3>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="text-center shadow-sm" style={{ backgroundColor: "#fff3e0" }}>
            <Card.Body>
              <h6 className="text-muted mb-2">
                {t(language, "profitAvgProfitHour")}
              </h6>
              <h3 className="mb-0" style={{ color: "#f57c00" }}>
                ${totals.avgProfitPerHour.toFixed(2)}
              </h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Profit Analysis Table */}
      <Card className="shadow-sm">
        <Card.Header>
          <h5 className="mb-0">
            {t(language, "profitDetailedAnalysis")}
          </h5>
        </Card.Header>
        <Card.Body>
          <Table responsive striped bordered hover>
            <thead className="table-light">
              <tr>
                <th>{t(language, "profitClient")}</th>
                <th className="text-center d-none d-md-table-cell">{t(language, "profitHours")}</th>
                <th className="text-end">{t(language, "profitInvoiceAmount")}</th>
                <th className="text-end d-none d-md-table-cell">{t(language, "profitRevenueHour")}</th>
                <th className="text-end d-none d-md-table-cell">{t(language, "profitLaborCost")}</th>
                <th className="text-end">{t(language, "profitProfitCol")}</th>
                <th className="text-end d-none d-md-table-cell">{t(language, "profitProfitHour")}</th>
                <th className="text-center">{t(language, "profitMargin")}</th>
              </tr>
            </thead>
            <tbody>
              {sortedMetrics.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center text-muted py-4">
                    {t(language, "profitNoClients")}
                  </td>
                </tr>
              ) : (
                <>
                  {sortedMetrics.map((client) => (
                    <tr key={client.id} style={{ backgroundColor: client.totalProfit < 0 ? "#ffebee" : "transparent" }}>
                      <td>
                        <strong>{client.name}</strong>
                      </td>
                      <td className="text-center d-none d-md-table-cell">{client.hours.toFixed(2)}h</td>
                      <td className="text-end">${client.invoiceAmount.toFixed(2)}</td>
                      <td className="text-end d-none d-md-table-cell">${client.revenuePerHour.toFixed(2)}</td>
                      <td className="text-end d-none d-md-table-cell" style={{ color: "#c62828" }}>
                        ${client.totalLaborCost.toFixed(2)}
                      </td>
                      <td className="text-end">
                        <strong style={{ color: client.totalProfit >= 0 ? "#388e3c" : "#c62828" }}>
                          ${client.totalProfit.toFixed(2)}
                        </strong>
                      </td>
                      <td className="text-end d-none d-md-table-cell">
                        <strong style={{ color: client.profitPerHour >= 0 ? "#388e3c" : "#c62828" }}>
                          ${client.profitPerHour.toFixed(2)}
                        </strong>
                      </td>
                      <td className="text-center">
                        <span className={`badge ${client.profitMargin >= 30 ? 'bg-success' : client.profitMargin >= 10 ? 'bg-warning' : 'bg-danger'}`}>
                          {client.profitMargin.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}

                  {/* Totals Row */}
                  <tr className="table-dark fw-bold" style={{ fontSize: '1.05rem' }}>
                    <td style={{ color: 'white' }}>TOTAL</td>
                    <td className="text-center d-none d-md-table-cell" style={{ color: 'white' }}>{totals.totalHours.toFixed(2)}h</td>
                    <td className="text-end" style={{ color: 'white' }}>${totals.totalRevenue.toFixed(2)}</td>
                    <td className="text-end d-none d-md-table-cell" style={{ color: 'white' }}>-</td>
                    <td className="text-end d-none d-md-table-cell" style={{ color: 'white' }}>${totals.totalLaborCost.toFixed(2)}</td>
                    <td className="text-end" style={{ color: 'white' }}>${totals.totalProfit.toFixed(2)}</td>
                    <td className="text-end d-none d-md-table-cell" style={{ color: 'white' }}>${totals.avgProfitPerHour.toFixed(2)}</td>
                    <td className="text-center" style={{ color: 'white' }}>{totals.overallMargin.toFixed(1)}%</td>
                  </tr>
                </>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Insights */}
      {sortedMetrics.length > 0 && (
        <Card className="mt-4 shadow-sm" style={{ backgroundColor: "#f3e5f5", border: "2px solid #8e24aa" }}>
          <Card.Body>
            <h5 style={{ color: "#6a1b9a" }}>
              {t(language, "profitInsights")}
            </h5>
            <Row>
              <Col md={6}>
                <p className="mb-2">
                  <strong>{t(language, "profitMostProfitable")}</strong>{" "}
                  {sortedMetrics[0].name} (${sortedMetrics[0].totalProfit.toFixed(2)})
                </p>
                <p className="mb-2">
                  <strong>{t(language, "profitBestProfitHour")}</strong>{" "}
                  {[...sortedMetrics].sort((a, b) => b.profitPerHour - a.profitPerHour)[0].name}
                  (${[...sortedMetrics].sort((a, b) => b.profitPerHour - a.profitPerHour)[0].profitPerHour.toFixed(2)}/h)
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-2">
                  <strong>{t(language, "profitUnprofitable")}</strong>{" "}
                  {sortedMetrics.filter(c => c.totalProfit < 0).length}
                </p>
                <p className="mb-2">
                  <strong>{t(language, "profitOverallMargin")}</strong>{" "}
                  <span className={totals.overallMargin >= 30 ? 'text-success' : totals.overallMargin >= 10 ? 'text-warning' : 'text-danger'}>
                    {totals.overallMargin.toFixed(1)}%
                  </span>
                </p>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}
